<template>
  <div class="w-full">
    <!-- Header -->
    <PageHeader
      title="Services"
      subtitle="Découvrez nos services partenaires pour booster votre activité"
    />

    <div class="w-full px-6 py-8">
      <!-- Services Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        <!-- EasyAI Service Card -->
        <div class="service-card">
          <div class="flex items-start justify-between mb-4">
            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xl">
              <i class="fas fa-camera"></i>
            </div>
            <span class="text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full uppercase tracking-wide">Recommandé</span>
          </div>

          <h3 class="text-base font-semibold text-black mb-2">EasyAI - Photos IA</h3>
          <p class="text-sm text-black leading-relaxed mb-4">
            Créez simultanément 10 photos IA en une dizaine de secondes.
            Générez des visuels professionnels pour vos produits grâce à
            l'intelligence artificielle.
          </p>

          <div class="space-y-2 mb-6 flex-1">
            <div class="flex items-center gap-2 text-sm text-black">
              <i class="fas fa-check-circle text-green-500"></i>
              <span>10 photos générées simultanément</span>
            </div>
            <div class="flex items-center gap-2 text-sm text-black">
              <i class="fas fa-check-circle text-green-500"></i>
              <span>Résultat en quelques secondes</span>
            </div>
            <div class="flex items-center gap-2 text-sm text-black">
              <i class="fas fa-check-circle text-green-500"></i>
              <span>Qualité professionnelle</span>
            </div>
          </div>

          <a
            href="https://www.easyai.fr"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-auto block w-full text-center px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg"
          >
            <i class="fas fa-external-link-alt mr-2"></i>
            Accéder au service
          </a>
        </div>

        <!-- Product manager Service Card -->
        <div class="service-card">
          <div class="flex items-start justify-between mb-4">
            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xl">
              <i class="fas fa-box"></i>
            </div>
          </div>

          <h3 class="text-base font-semibold text-black mb-2">Product manager</h3>
          <p class="text-sm text-black leading-relaxed mb-4 flex-1">
            Importez vos fiches produits et vos photos en quelques clics sur Efashion.
          </p>

          <button
            type="button"
            class="mt-auto block w-full text-center px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-wait"
            :disabled="isCheckingCgu || isAcceptingCgu"
            @click="handleProductManagerClick"
          >
            <i class="fas fa-external-link-alt mr-2"></i>
            Accéder au service
          </button>
        </div>

        <!-- PrestaShop catalogue connector -->
        <div class="service-card">
          <div class="flex items-start justify-between mb-4">
            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white text-xl">
              <i class="fas fa-sync-alt"></i>
            </div>
          </div>
          <h3 class="text-base font-semibold text-black mb-2">
            Import PrestaShop
          </h3>
          <p class="text-sm text-black leading-relaxed mb-4 flex-1">
            Connectez votre boutique, complétez les fiches et importez votre
            catalogue sur eFashion.
          </p>
          <router-link
            :to="{ name: 'services/prestashop' }"
            class="mt-auto block w-full text-center px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg"
          >
            <i class="fas fa-arrow-right mr-2"></i>
            Configurer l'import
          </router-link>
        </div>

        <!-- Shopify catalogue connector -->
        <div class="service-card">
          <div class="flex items-start justify-between mb-4">
            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xl">
              <i class="fab fa-shopify"></i>
            </div>
          </div>
          <h3 class="text-base font-semibold text-black mb-2">
            Import Shopify
          </h3>
          <p class="text-sm text-black leading-relaxed mb-4 flex-1">
            Connectez votre boutique Shopify en un clic et consultez votre
            catalogue depuis eFashion.
          </p>
          <router-link
            :to="{ name: 'services/shopify' }"
            class="mt-auto block w-full text-center px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 text-sm font-semibold shadow-md hover:shadow-lg"
          >
            <i class="fas fa-arrow-right mr-2"></i>
            Configurer l'import
          </router-link>
        </div>
      </div>
    </div>

    <ConfirmModal
      :show="showProductManagerCguModal"
      title="Conditions d'utilisation"
      :message="cguModalMessage"
      confirm-text="Continuer"
      confirm-button-class="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
      :confirm-disabled="!productManagerCguAccepted || isAcceptingCgu"
      max-width-class="max-w-lg"
      @confirm="confirmProductManagerAccess"
      @cancel="closeProductManagerCguModal"
    >
      <template #extra>
        <div class="space-y-4">
          <p class="text-sm text-black leading-relaxed">
            Veuillez consulter les
            <a
              :href="cguUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 font-medium hover:text-blue-800 underline"
              @click.stop
            >
              Conditions d'utilisation
            </a>
            avant de poursuivre.
          </p>
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              v-model="productManagerCguAccepted"
              type="checkbox"
              class="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span class="text-sm text-black leading-relaxed">
              J'ai lu et j'accepte les Conditions d'utilisation
            </span>
          </label>
        </div>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import PageHeader from '@/components/layout/seller/PageHeader.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import {
  acceptProductManagerCgu,
  getProductManagerCguStatus,
} from '@/api/productManagerCgu'
import { useNotification } from '@/composables/useNotification'

const PRODUCT_MANAGER_URL =
  'https://drive.google.com/drive/folders/1Buy87GG5DDZcEG6rE3D657nhavMrSYY_'
const cguUrl = `${process.env.BASE_URL}docs/cgu-product-manager.pdf`

const cguModalMessage = [
  'Cet outil vous permet de transférer vos fiches produits depuis une plateforme source vers votre compte Efashion.',
  '',
  "En utilisant cet outil, vous confirmez que vous êtes autorisé à utiliser et transférer les contenus concernés et demandez à Efashion d'effectuer les opérations techniques nécessaires à leur importation.",
].join('\n')

const { error: showError } = useNotification()

const showProductManagerCguModal = ref(false)
const productManagerCguAccepted = ref(false)
const hasAcceptedCurrentCgu = ref(false)
const isCheckingCgu = ref(false)
const isAcceptingCgu = ref(false)

const openProductManagerService = () => {
  window.open(PRODUCT_MANAGER_URL, '_blank', 'noopener,noreferrer')
}

const openProductManagerCguModal = () => {
  productManagerCguAccepted.value = false
  showProductManagerCguModal.value = true
}

const closeProductManagerCguModal = () => {
  showProductManagerCguModal.value = false
  productManagerCguAccepted.value = false
}

const loadCguStatus = async () => {
  isCheckingCgu.value = true
  try {
    const status = await getProductManagerCguStatus()
    hasAcceptedCurrentCgu.value = Boolean(status?.accepted)
  } catch {
    hasAcceptedCurrentCgu.value = false
  } finally {
    isCheckingCgu.value = false
  }
}

const handleProductManagerClick = async () => {
  if (isAcceptingCgu.value) return

  if (hasAcceptedCurrentCgu.value) {
    openProductManagerService()
    return
  }

  // Recharge au clic pour couvrir un changement de session / version
  isCheckingCgu.value = true
  try {
    const status = await getProductManagerCguStatus()
    hasAcceptedCurrentCgu.value = Boolean(status?.accepted)
    if (hasAcceptedCurrentCgu.value) {
      openProductManagerService()
      return
    }
  } catch {
    // Si le statut est indisponible, on affiche quand même la popup
  } finally {
    isCheckingCgu.value = false
  }

  openProductManagerCguModal()
}

const confirmProductManagerAccess = async () => {
  if (!productManagerCguAccepted.value || isAcceptingCgu.value) return

  isAcceptingCgu.value = true
  try {
    const status = await acceptProductManagerCgu()
    hasAcceptedCurrentCgu.value = Boolean(status?.accepted)
    closeProductManagerCguModal()
    openProductManagerService()
  } catch (err: unknown) {
    const message =
      err instanceof Error
        ? err.message
        : "Impossible d'enregistrer l'acceptation des CGU"
    showError(message)
  } finally {
    isAcceptingCgu.value = false
  }
}

onMounted(() => {
  void loadCguStatus()
})
</script>

<style scoped>
.service-card {
  @apply bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full min-h-[25rem];
}
</style>
